/**
 * Iter XX.6 — The Cricket Journey.
 *
 * Season Card modal. Opened from a small "🏏 Season" link in the chat
 * header. Fetches /api/season/summary on open and renders the six
 * grounded fields plus the identity arc. If a review is due, shows
 * a "Generate My Review" button that calls /api/season/review/generate
 * and updates the modal in-place.
 *
 * Every field is grounded — the backend returns `null` when there's not
 * enough evidence and the UI renders "Not enough matches yet" rather
 * than a fabricated moment. Missing memory > fake memory.
 */
import { useCallback, useEffect, useState } from "react";
import { X, Sparkles, TrendingUp } from "lucide-react";
import api from "../lib/api";

function fmtPct(n) {
  if (n === null || n === undefined) return null;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

function Row({ label, value, testid }) {
  return (
    <div className="py-2" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
        {label}
      </p>
      <p className="text-sm text-neutral-200 leading-relaxed">
        {value || <span className="text-neutral-600">— not enough evidence yet</span>}
      </p>
    </div>
  );
}

export default function SeasonCard({ open, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    setNeedsAuth(false);
    try {
      const r = await api.get("/season/summary");
      setData(r.data || null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Iter XX.49 — friendly logged-out state. Never surface
        // developer wording like "Not authenticated".
        setNeedsAuth(true);
      } else {
        setErr("Couldn't load your season right now — try again in a bit.");
      }
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleGenerateReview = async () => {
    setGenerating(true);
    try {
      await api.post("/season/review/generate");
      await load();
    } catch {
      /* swallow — user can retry */
    } finally {
      setGenerating(false);
    }
  };

  if (!open) return null;

  const summary = data?.summary;
  const reviewDue = data?.review_due;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-labelledby="season-card-title"
      onClick={onClose}
      data-testid="season-card-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-neutral-800 bg-[#0d1613] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="label-eyebrow text-emerald-300 mb-1">How you&apos;re improving</p>
            <h2 id="season-card-title" className="text-2xl font-display font-light">
              Your Cricket Journey
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-neutral-500 hover:text-neutral-200"
            data-testid="season-card-close"
          >
            <X size={18} />
          </button>
        </div>

        {needsAuth && (
          <div className="text-center py-4" data-testid="season-card-signed-out">
            <div className="text-4xl mb-3">🏏</div>
            <p className="text-sm text-neutral-200 mb-1 font-medium">Your Cricket Journey</p>
            <p className="text-sm text-neutral-400 leading-relaxed mb-6">
              Track your entire season.<br/>
              See how you're improving over time.<br/>
              Sign in to begin.
            </p>
            <a
              href="/login?return_to=/chat"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-neutral-950 text-sm font-medium hover:bg-emerald-400 transition-colors"
              data-testid="season-card-signed-out-cta"
            >
              Continue with Google
            </a>
          </div>
        )}

        {err && !needsAuth && (
          <p className="text-sm text-neutral-400" data-testid="season-card-error">
            {err}
          </p>
        )}

        {!err && !needsAuth && !summary && (
          <p className="text-sm text-neutral-500">Loading…</p>
        )}

        {summary && (
          <div className="divide-y divide-neutral-800/70">
            <Row
              label="Matches"
              value={summary.matches > 0 ? `${summary.matches}` : null}
              testid="season-matches"
            />
            <Row
              label="Biggest improvement"
              value={
                summary.biggest_improvement
                  ? `${summary.biggest_improvement.label} · avg ${summary.biggest_improvement.avg_earlier} → ${summary.biggest_improvement.avg_recent}`
                  : null
              }
              testid="season-biggest-improvement"
            />
            <Row
              label="Recurring pattern"
              value={summary.recurring_pattern}
              testid="season-recurring-pattern"
            />
            <Row
              label="Strength"
              value={summary.strength}
              testid="season-strength"
            />
            <Row
              label="Confidence trend"
              value={
                summary.confidence_trend_pct !== null &&
                summary.confidence_trend_pct !== undefined
                  ? (
                    <span className="inline-flex items-center gap-1">
                      <TrendingUp size={14} className="text-emerald-400" />
                      {fmtPct(summary.confidence_trend_pct)}
                    </span>
                  )
                  : null
              }
              testid="season-confidence-trend"
            />
            <Row
              label="Most memorable moment"
              value={summary.most_memorable_moment}
              testid="season-memorable-moment"
            />
            {summary.identity_arc && (
              <div className="py-4" data-testid="season-identity-arc">
                <div className="flex items-start gap-2">
                  <Sparkles size={16} className="text-emerald-400 flex-none mt-0.5" />
                  <p className="text-sm text-emerald-100 italic leading-relaxed">
                    {summary.identity_arc}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {reviewDue && (
          <div
            className="mt-4 rounded-xl border border-emerald-800/40 bg-emerald-950/30 p-3"
            data-testid="season-review-due"
          >
            <p className="text-sm text-emerald-100 mb-2">
              🏏 Your {reviewDue.threshold}-match season review is ready.
            </p>
            <button
              onClick={handleGenerateReview}
              disabled={generating}
              className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
              data-testid="season-generate-review"
            >
              {generating ? "Generating…" : "Generate my review"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
